/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import axios, { AxiosError } from 'axios'
import { redirect, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  InputAdornment,
  Card,
  CardHeader,
  Avatar
} from '@material-ui/core'
import { Cancel, NoteAdd, Save, ArrowDropDown, ArrowDropUp, Schedule, Delete } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import UserContext from '../../../contexts/User'
import EntityContext from '../../../contexts/Entity'
import ThirdsContext from '../../../contexts/Third'
import { EntityContextType } from '../../../schemas/Entity'
import { UserContextType } from '../../../schemas/User'
import { ThirdsContextType } from '../../../schemas/Third'
import { ElectronicBillFormSchema, ScheduleForm } from '../../../schemas/ElectronicBill'
import ElectronicBillService from '../../../services/ElectronicBill'
import Utils from '../../../utils'
import ItemTable from '../Common/ItemTable'
import PageContainer from '../Common/PageContainer'
import FormActions from '../Common/FormActions'
import SelectField from '../Common/SelectField'
import ThirdService from '../../../services/Third'
import ElectronicBillMapper from '../../../mappers/ElectronicBill/electronicBill.mapper'
import ItemService from '../../../services/Item'
import PlemsiService from '../../../services/Plemsi'
import { NumericFormat } from 'react-number-format'
import { urls } from '../../../urls'
import ScheduleModal from './ScheduleModal'
import permissions from '../../../permissions.json'
import moment from 'moment'

const WAY_TO_PAY_CONTADO = '1'
const WAY_TO_PAY_CREDITO = '2'

/** Returns price field helper text based on the tax-included price feature flag. */
const getPriceHelperText = (): string => {
  if (Utils.isPriceIncludingTaxEnabled()) {
    return 'Precio final (impuestos incluidos)'
  }
  return 'Precio sin impuestos'
}

const getToday = () => moment().format('YYYY-MM-DD')
const getTomorrow = () => moment().add(1, 'day').format('YYYY-MM-DD')

// -------------- Styles ---------------
const useStyles = makeStyles((theme: Theme) => ({
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  },
  sectionDivider: {
    marginTop: theme.spacing(1),
  },
  alignRight: {
    textAlign: 'right',
  },
  addButton: {
    width: '100%',
    minHeight: 48,
    color: '#ffff',
  },
  clickable: {
    cursor: 'pointer',
  },
  avatarSchedule: {
    color: 'primary',
    backgroundColor: '#ffff',
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Facturación electrónica'
  },
  body: {
    field: {
      date: {
        name: 'date',
        helperText: 'Fecha de facturación',
        placeholder: 'Fecha'
      },
      orderReference: {
        name: 'orderReference',
        helperText: 'Escriba la referencia de orden',
        placeholder: 'Código de orden'
      },
      third: {
        name: 'third',
        helperText: 'Escoja el tercero',
        placeholder: 'Tercero'
      },
      wayToPay: {
        name: 'wayToPay',
        helperText: 'Escoja una forma de pago',
        options: [
          { description: 'Contado', code: '1' },
          { description: 'Crédito', code: '2' },
        ]
      },
      paymentMethod: {
        name: 'paymentMethod',
        helperText: 'Escoja un método de pago',
        options: [
          { description: 'Cheque', code: '20' },
          { description: 'Consignación bancaria', code: '42' },
          { description: 'Efectivo', code: '10' },
          { description: 'Instrumento no definido', code: '1' },
          { description: 'Pago y depósito pre acordado (PPD)', code: '34' },
          { description: 'Tarjeta Crédito', code: '48' },
          { description: 'Tarjeta Débito', code: '49' },
        ]
      },
      paymentDueDate: {
        name: 'paymentDueDate',
        helperText: {
          contado: 'Coincide con la fecha del documento',
          credito: 'Debe ser posterior a la fecha actual',
          default: 'Fecha de pago'
        },
        placeholder: 'AAAA/MM/DD'
      },
      note: {
        name: 'note',
        helperText: 'Nota para la factura',
        placeholder: 'Nota para la factura'
      },
      item: {
        name: 'currentItem',
        helperText: 'Escoja el producto a facturar',
        options: [
          {
            unitMeasure: { code: 12, description: 'Unidad' },
            description: 'Producto de prueba',
            code: '1',
            price: 2500
          }
        ]
      },
      itemType: {
        name: 'currentItemType',
        helperText: 'Tipo de producto',
        options: [
          { description: 'UNSPSC', code: '1' },
          { description: 'GTIN', code: '2' },
          { description: 'Partida Arancelarias', code: '3' },
          { description: 'Estándar de adopción del contribuyente', code: '4' }
        ]
      },
      price: {
        name: 'currentPrice',
        helperText: 'Precio sin impuestos',
        placeholder: 'Precio'
      },
      quantity: {
        name: 'currentQuantity',
        helperText: 'Cantidad',
        placeholder: 'Cantidad'
      },
      detailedDescription: {
        name: 'currentDetailedDescription',
        helperText: 'Descripción detallada (opcional)',
        placeholder: 'Descripción detallada del producto'
      },
      applyTaxes: {
        name: 'applyTaxes',
        helperText: 'Aplicar impuestos'
      },
      tax: {
        name: 'selectedTax',
        helperText: 'Escoja el impuesto',
        options: [
          { code: '1', description: 'IVA' },
          { code: '4', description: 'Impuesto al consumo' }
        ]
      },
      percent: {
        name: 'currentPercentTax',
        helperText: 'Porcentaje',
        placeholder: '%',
        options: [
          { code: 0, description: '0%' },
          { code: 5, description: '5%' },
          { code: 8, description: '8%' },
          { code: 19, description: '19%' },
        ]
      },
      total: {
        name: 'total',
        helperText: 'Total sin impuestos',
        placeholder: 'Total sin impuestos'
      },
      totalTaxes: {
        name: 'totalTaxes',
        helperText: 'Total de impuestos',
        placeholder: 'Total de impuestos'
      },
      totalToPay: {
        name: 'totalToPay',
        helperText: 'Total final',
        placeholder: 'Total'
      }
    },
    subtitles: {
      payment: 'Información acerca del pago',
      itemsTable: 'Tabla de productos a facturar',
      total: 'Información acerca de los totales de la factura',
      scheduleTitle: 'Programar Factura',
      scheduleDescription: 'automatice su factura de forma periódica'
    },
    buttons: {
      addProduct: {
        title: 'Agregar'
      }
    },
    errors: {
      incompletedScheduleForm: 'Debe completar los campos requeridos para programar una factura'
    }
  }
}

// ------------ Init state -----------
interface State {
  form: ElectronicBillFormSchema
  applyTaxes: boolean
  items: any[]
  selectedItems: any[]
  thirds: any[]
  preview?: string
  loading: boolean
  disabledForm: boolean
  status: boolean
  thirdSelected?: string
  suggestionsThirds: any[]
  showScheduleModal: boolean
  scheduledBill: boolean
}

const getPaymentDueDateHelperText = (wayToPayCode: string) => {
  if (wayToPayCode === WAY_TO_PAY_CONTADO) {
    return texts.body.field.paymentDueDate.helperText.contado
  }
  if (wayToPayCode === WAY_TO_PAY_CREDITO) {
    return texts.body.field.paymentDueDate.helperText.credito
  }
  return texts.body.field.paymentDueDate.helperText.default
}

const getInitState = (): State => ({
  form: {
    date: getToday(),
    orderReference: '',
    third: undefined,
    wayToPay: { code: '', description: '' },
    paymentMethod: { code: '', description: '' },
    paymentDueDate: '',
    note: '',
    currentItem: undefined,
    currentItemType: {
      code: '',
      description: ''
    },
    currentPrice: 0,
    currentQuantity: 0,
    total: 0,
    totalTaxes: 0,
    totalToPay: 0,
    selectedTax: { code: '', description: '' },
    currentPercentTax: 0,
    currentDetailedDescription: ''
  },
  applyTaxes: false,
  items: [],
  selectedItems: [],
  thirds: [],
  preview: undefined,
  loading: false,
  disabledForm: false,
  status: false,
  thirdSelected: '',
  suggestionsThirds: [],
  showScheduleModal: false,
  scheduledBill: false
})

export default function ElectronicBillForm() {
  const classes = useStyles()
  const { search } = useLocation()

  const [state, setState] = useState(getInitState)
  const { userContext } = React.useContext(
    UserContext
  ) as UserContextType

  const { entityContext } = React.useContext(
    EntityContext
  ) as EntityContextType

  const { setThirdsContext } = React.useContext(
    ThirdsContext
  ) as ThirdsContextType

  React.useEffect(() => {

    async function loadData() {
      try {
        //Search bill on URL
        const params = Utils.parseQueryString(search)
        if (params.length > 0) {
          let number = 0
          let copy = false
          let billPlemsi = undefined
          let thirds = []
          let items = []

          params.forEach(param => {
            if (param.number) {
              number = Number(param.number)
            }

            if (param.copy) {
              copy = true
            }
          })

          setState({
            ...state,
            loading: true
          })

          const electronicBillService = new ElectronicBillService()
          const bill = await electronicBillService.getBillByNumber(number)

          if (!copy) {
            const plemsiService = new PlemsiService()
            billPlemsi = await plemsiService.getBill(entityContext.apiKeyPlemsi ?? '', bill.form.number ?? 0, entityContext.prefix)
            thirds = [...state.thirds, bill.form.third]
            items = [...state.items, bill.items]
          } else {
            const thirdService = new ThirdService()
            const itemService = new ItemService()
            const thirdsRes = await thirdService.getThirds()
            const itemsRes = await itemService.getItems()
            setThirdsContext(thirdsRes.thirds) // save in context
            localStorage.setItem('thirds', JSON.stringify(thirdsRes.thirds))
            thirds = thirdsRes.thirds
            items = itemsRes.items
          }

          let form = bill.form
          if (copy) {
            const today = getToday()
            form = {
              ...bill.form,
              date: today,
              paymentDueDate: today
            }
          }

          setState({
            ...state,
            form,
            thirdSelected: bill.form.third?.name !== undefined ? `${bill.form.third?.name} ${bill.form.third?.lastname}` : `${bill.form.third?.businessName}`,
            thirds,
            suggestionsThirds: thirds,
            items,
            selectedItems: bill.items,
            loading: false,
            disabledForm: !copy,
            preview: billPlemsi
          })

        } else {
          // Load service
          const thirdService = new ThirdService()
          const itemService = new ItemService()
          const thirds = await thirdService.getThirds()
          const items = await itemService.getItems()

          setState({
            ...state,
            thirds: thirds.thirds,
            suggestionsThirds: thirds.thirds,
            items: items.items,
            selectedItems: []
          })
          setThirdsContext(thirds.thirds) // save in context
          localStorage.setItem('thirds', JSON.stringify(thirds.thirds))
        }
      } catch (error) {

        setState({
          ...state,
          loading: false
        })

        if (axios.isAxiosError(error)) {
          const serverError = error as AxiosError<ServerError>
          if (serverError && serverError.response) {
            return toast.error(serverError.response.data.message || error.toString())
          }
        }
      }
    }

    loadData()
  }, [])

  const handleChangeSelectValue = (name: string, value: string | number) => {
    setState({
      ...state,
      form: {
        ...state.form,
        [name]: value
      }
    })
  }

  // HandleChangeSelect is the handler to update the state
  // of the state, while the user change the options 
  // on an input select.
  const handleChangeSelect = (
      name: string,
      item: {
        code: string,
        description: string,
        price?: number,
        itemType?: any,
        name?: string,
        lastname?: string,
        businessName?: string
      }) => {
    if (item.price && item.itemType) {
      setState({
        ...state,
        form: {
          ...state.form,
          [name]: item,
          currentPrice: item.price,
          currentItemType: {
            code: item.itemType.code,
            description: item.itemType.description
          }
        }
      })
    } else if (name === texts.body.field.third.name) {
      setState({
        ...state,
        form: {
          ...state.form,
          [name]: item
        },
        thirdSelected: item.name !== undefined ? `${item.name} ${item.lastname}` : `${item.businessName}`,
        status: false
      })
    } else if (name === texts.body.field.wayToPay.name) {
      const today = getToday()
      setState({
        ...state,
        form: {
          ...state.form,
          [name]: item,
          date: today,
          paymentDueDate: item.code === WAY_TO_PAY_CONTADO ? today : ''
        }
      })
    } else {
      setState({
        ...state,
        form: {
          ...state.form,
          [name]: item
        }
      })
    }
  }

  // HandleChange is the handler to update the state
  // of the state, while the user writes on an input.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name: string
    value: unknown
  }>) => {
    const { name, value } = e.target
    setState({
      ...state,
      form: {
        ...state.form,
        [name]: value
      }
    })
  }

  const handleSuggestions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    const suggestionsThirds = state.thirds.filter(third => {
      if (third.name?.toLowerCase().includes(value.toLowerCase())) {
        return third
      } else if (third.lastname?.toLowerCase().includes(value.toLowerCase())) {
        return third
      } else if (third.businessName?.toLowerCase().includes(value.toLowerCase())) {
        return third
      }
      return false
    })

    setState({
      ...state,
      thirdSelected: value,
      suggestionsThirds,
      status: value.length > 0 ? true : false
    })
  }

  // HandleSubmit is the handler to verify and send
  // the state to redux (redux send the info to backend)
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setState({
      ...state,
      loading: true
    })

    try {
      // Validations
      if (!state.form.third) throw new Error('Debe seleccionar un tercero del listado')

      const today = getToday()
      let formToSubmit = { ...state.form, date: today }

      if (formToSubmit.wayToPay.code === WAY_TO_PAY_CONTADO) {
        formToSubmit = { ...formToSubmit, paymentDueDate: today }
      } else if (formToSubmit.wayToPay.code === WAY_TO_PAY_CREDITO) {
        if (!formToSubmit.paymentDueDate || formToSubmit.paymentDueDate <= today) {
          throw new Error('La fecha de pago debe ser posterior a la fecha actual')
        }
      }

      const res = ElectronicBillMapper.formToSchema(formToSubmit, state.selectedItems)
      const electronicBillService = new ElectronicBillService()
      const plemsiService = new PlemsiService()
      const bill = await electronicBillService.saveBill(res)
      const billPlemsi = await plemsiService.getBill(bill.entityInformation.apikey, bill.entityInformation.number, entityContext.prefix ?? '')
      setState({
        ...getInitState(),
        applyTaxes: false,
        items: state.items,
        selectedItems: [],
        thirds: state.thirds,
        form: {
          ...getInitState().form,
          number: bill.entityInformation.number // Asignar el número de la factura creada
        },
        preview: billPlemsi,
        loading: false
      })

      redirect(urls.app.main.electronicBill.form)
      return toast.success(`Factura creada con éxito`)
    } catch (error) {

      setState({
        ...state,
        loading: false
      })
      
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message || error.toString())
        }
      } else if (error instanceof Error) {
        return toast.error(error.message)
      }
    }
  }

  // HandleTable is the handler to add and remove items
  // from the concept table, it also adds or subtracts 
  // the value of the total receipt.
  const handleTable = (func: string, selectedItem?: any) => {
    if (func === 'ADD') {
      const item = Utils.buildItem(state.form, state.applyTaxes)
      const total = Utils.roundToMaxDecimals(state.form.total + item.total)
      const totalTaxes = Utils.roundToMaxDecimals(state.form.totalTaxes + item.taxes[0].taxAmount)
      const totalToPay = Utils.roundToMaxDecimals(total + totalTaxes)
      const items = state.selectedItems
      items.push(item)
      setState({
        ...state,
        form: {
          ...state.form,
          total: total,
          totalTaxes: totalTaxes,
          totalToPay: totalToPay,
          currentItemType: {
            code: '',
            description: ''
          },
          currentPrice: 0,
          currentQuantity: 0,
          selectedTax: { code: '', description: '' },
          currentPercentTax: 0,
          currentDetailedDescription: ''
        },
        applyTaxes: false,
        selectedItems: items
      })
    }

    if (func === 'REMOVE') {
      const total = Utils.roundToMaxDecimals(state.form.total - selectedItem.total)
      const totalTaxes = Utils.roundToMaxDecimals(state.form.totalTaxes - selectedItem.taxes[0].taxAmount)
      const totalToPay = Utils.roundToMaxDecimals(total + totalTaxes)
      const items = state.selectedItems.filter(item => item.code !== selectedItem.code)
      setState({
        ...state,
        form: {
          ...state.form,
          total: total,
          totalTaxes: totalTaxes,
          totalToPay: totalToPay,
        },
        selectedItems: items
      })
    }
  }

  const onApplyTaxes = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setState({
      ...state,
      applyTaxes: checked
    })
  }

  const cancel = () => {
    setState({
      ...getInitState(),
      applyTaxes: false,
      items: state.items,
      selectedItems: [],
      thirds: state.thirds,
      preview: undefined,
      loading: false
    })
  }

  const onPreviewBill = async () => {
    if (!state.disabledForm) {
      setState({
        ...state,
        preview: undefined
      })
    }

    // Si tenemos un número de factura, obtener el HTML y generar PDF
    console.log('🔍 onPreviewBill - state.form.number:', state.form.number)
    console.log('🔍 onPreviewBill - state.form:', state.form)
    
    if (state.form.number) {
      try {
        const electronicBillService = new ElectronicBillService()
        console.log('🔍 onPreviewBill - Calling service with number:', state.form.number)
        const htmlContent = await electronicBillService.getElectronicInvoiceHTML(state.form.number)
        
        // Generar el PDF con el HTML obtenido
        const invoiceNumber = state.form.number.toString()
        Utils.generateElectronicInvoicePDF(htmlContent, invoiceNumber)
        
        // Mostrar mensaje de éxito
        toast.success('PDF de factura generado correctamente')
      } catch (error) {
        console.error('Error generando PDF de factura:', error)
        toast.error(`Error generando PDF de factura: ${error}`)
      }
    } else {
      console.log('❌ onPreviewBill - No hay número de factura disponible')
      toast.error('No hay número de factura disponible')
    }
  }

  const handleScheduleModal = (show: boolean) => { 
    setState({
      ...state,
      showScheduleModal: show
    })
  }

  const handleScheduleForm = (formSchedule: ScheduleForm) => {
    if (
      !formSchedule.name ||
      !formSchedule.startDate ||
      !formSchedule.intervalDays
    ) {
      return toast.error(texts.body.errors.incompletedScheduleForm)
    }

    setState({
      ...state,
      form: {
        ...state.form,
        scheduleForm: formSchedule
      },
      scheduledBill: true,
      showScheduleModal: false
    })
  }

  return (
    <PageContainer title={texts.header.description}>

      {/* ----- Body ------*/}
      <form onSubmit={(e) => handleSubmit(e)}>
        <Grid container spacing={3}>

          {/* ----- Form: order reference ------*/}
          <Grid item xs={12}>
            <TextField
              required={true}
              name={texts.body.field.orderReference.name}
              label={texts.body.field.orderReference.placeholder}
              value={state.form.orderReference}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.orderReference.helperText}
              disabled={state.disabledForm}
            />
          </Grid>

          {/* ----- Form: third ------*/}
          <Grid item xs={12}>
            <TextField 
              name="thirdSelected"
              label={texts.body.field.third.placeholder}
              value={state.thirdSelected}
              autoComplete='off'
              variant="outlined"
              fullWidth
              disabled={state.disabledForm}
              onChange={handleSuggestions}
              InputProps={{
                endAdornment: <InputAdornment position="end">
                  <IconButton aria-label="Mostrar terceros" onClick={() => setState({...state, status: !state.status})}>
                    {
                      state.status ? 
                      (
                        <ArrowDropUp fontSize={"small"} color="secondary" />
                      ) :
                      (
                        <ArrowDropDown fontSize={"small"} color="primary" />
                      )
                    }
                  </IconButton>
                </InputAdornment>,
              }}
            />
            {
              state.status === true && (
                <Box sx={{
                  width: '100%',
                  maxHeight: 100,
                  display: 'relative',
                  zIndex: 1,
                  bgcolor: '#E1E1E1',
                  overflow: 'auto'
                }}>
                  {
                    state.suggestionsThirds.map((third) =>
                      <MenuItem
                        key={third.document}
                        onClick={() => handleChangeSelect(texts.body.field.third.name, third)}
                      >
                        {third.name !== undefined ? `${third.name} ${third.lastname}` : `${third.businessName}`}
                      </MenuItem>
                    )
                  }
                </Box>
              )
            }
            <FormHelperText style={{ margin: '3px 14px 0' }}>{texts.body.field.third.helperText}</FormHelperText>
          </Grid>

          {/* ----- Form: payment ------*/}

          <Grid item md={4} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.wayToPay.name}
              label="Forma de pago"
              value={state.form.wayToPay.code}
              helperText={texts.body.field.wayToPay.helperText}
              disabled={state.disabledForm}
            >
              {
                texts.body.field.wayToPay.options.map((item) =>
                  <MenuItem
                    key={item.description}
                    value={item.code}
                    onClick={() => handleChangeSelect(texts.body.field.wayToPay.name, item)}
                  >
                    {item.description}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: payment method ------*/}
          <Grid item md={4} sm={6} xs={12}>
            <SelectField
              name={texts.body.field.paymentMethod.name}
              label="Método de pago"
              value={state.form.paymentMethod.code}
              helperText={texts.body.field.paymentMethod.helperText}
              disabled={state.disabledForm}
            >
              {
                texts.body.field.paymentMethod.options.map((item) =>
                  <MenuItem
                    key={item.description}
                    value={item.code}
                    onClick={() => handleChangeSelect(texts.body.field.paymentMethod.name, item)}
                  >
                    {item.description}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: payment due date ------*/}
          <Grid item md={4} sm={12} xs={12}>
            <TextField
              required={true}
              name={texts.body.field.paymentDueDate.name}
              label="Fecha de pago"
              value={state.form.paymentDueDate}
              variant='outlined'
              type='date'
              fullWidth
              onChange={handleChange}
              helperText={getPaymentDueDateHelperText(state.form.wayToPay.code)}
              InputLabelProps={{ shrink: true }}
              disabled={state.disabledForm || state.form.wayToPay.code !== WAY_TO_PAY_CREDITO}
              inputProps={
                state.form.wayToPay.code === WAY_TO_PAY_CREDITO
                  ? { min: getTomorrow() }
                  : undefined
              }
            />
          </Grid>

          {/* ----- Form: note ------*/}

          <Grid item xs={12}>
            <TextField
              required={true}
              name={texts.body.field.note.name}
              label={texts.body.field.note.placeholder}
              value={state.form.note}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.note.helperText}
              disabled={state.disabledForm}
            />
          </Grid>

          {/* ----- Form: item ------*/}

          <Grid item xs={12}>
            <Typography
              variant='h6'
            >
              {texts.body.subtitles.itemsTable}
            </Typography>
            <Divider className={classes.sectionDivider} />
          </Grid>

          <Grid item md={8} sm={12} xs={12}>
            <SelectField
              name={texts.body.field.item.name}
              label="Producto"
              value={state.form.currentItem?.code ?? ''}
              helperText={texts.body.field.item.helperText}
              disabled={state.disabledForm}
            >
              {
                state.items.map((item) =>
                  <MenuItem
                    key={item.description}
                    value={item.code}
                    onClick={() => handleChangeSelect(texts.body.field.item.name, item)}
                  >
                    {item.description}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: item type ------*/}
          <Grid item md={4} sm={12} xs={12}>
            <SelectField
              name={texts.body.field.itemType.name}
              label="Tipo de producto"
              value={state.form.currentItemType.code}
              helperText={texts.body.field.itemType.helperText}
              disabled={state.disabledForm}
            >
              {
                texts.body.field.itemType.options.map((item) =>
                  <MenuItem
                    key={item.description}
                    value={item.code}
                    onClick={() => handleChangeSelect(texts.body.field.itemType.name, item)}
                  >
                    {item.description}
                  </MenuItem>
                )
              }
            </SelectField>
          </Grid>

          {/* ----- Form: price ------*/}
          <Grid item md={8} sm={6} xs={12}>
            <NumericFormat
              required={true}
              name={texts.body.field.price.name}
              label={texts.body.field.price.placeholder}
              value={state.form.currentPrice ?? ''}
              onValueChange={(values) =>
                setState({
                  ...state,
                  form: {
                    ...state.form,
                    currentPrice: Utils.roundToMaxDecimals(values.floatValue ?? 0)
                  }
                })
              }
              decimalScale={Utils.getMaxPriceDecimals()}
              thousandSeparator="."
              decimalSeparator=","
              prefix="COP "
              allowNegative={false}
              customInput={TextField}
              variant="outlined"
              fullWidth
              inputMode="decimal"
              helperText={getPriceHelperText()}
              disabled={state.disabledForm}
            />
          </Grid>

          {/* ----- Form: quantity ------*/}
          <Grid item md={4} sm={6} xs={12}>
            <TextField
              required={true}
              name={texts.body.field.quantity.name}
              label={texts.body.field.quantity.placeholder}
              value={state.form.currentQuantity}
              variant='outlined'
              type='number'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.quantity.helperText}
              disabled={state.disabledForm}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name={texts.body.field.detailedDescription.name}
              label={texts.body.field.detailedDescription.placeholder}
              value={state.form.currentDetailedDescription}
              variant='outlined'
              fullWidth
              multiline
              minRows={2}
              onChange={handleChange}
              helperText={texts.body.field.detailedDescription.helperText}
              disabled={state.disabledForm}
            />
          </Grid>

          <Grid item xs={12}>
            {/* ----- apply taxes check ------*/}
            <FormControlLabel
              control={<Checkbox checked={state.applyTaxes} name={texts.body.field.applyTaxes.name} value={state.applyTaxes} onChange={(e) => onApplyTaxes(e)} color="primary" />}
              label={texts.body.field.applyTaxes.helperText}
              disabled={state.disabledForm}
            />
          </Grid>

          {
            state.applyTaxes ?
              (
                <React.Fragment>
                  <Grid item md={6} sm={6} xs={12}>
                    <SelectField
                      name={texts.body.field.tax.name}
                      label="Impuesto"
                      value={state.form.selectedTax.code}
                      helperText={texts.body.field.tax.helperText}
                    >
                      {
                        texts.body.field.tax.options.map((item) =>
                          <MenuItem
                            key={item.description}
                            value={item.code}
                            onClick={() => handleChangeSelect(texts.body.field.tax.name, item)}
                          >
                            {item.description}
                          </MenuItem>
                        )
                      }
                    </SelectField>
                  </Grid>

                  {/* ----- Form: percent ------*/}
                  <Grid item md={3} sm={6} xs={12}>
                    <SelectField
                      name={texts.body.field.percent.name}
                      label="Porcentaje"
                      value={state.form.currentPercentTax}
                      helperText={texts.body.field.percent.helperText}
                    >
                      {
                        texts.body.field.percent.options.map((item) =>
                          <MenuItem
                            key={item.description}
                            value={item.code}
                            onClick={() => handleChangeSelectValue(texts.body.field.percent.name, item.code)}
                          >
                            {item.description}
                          </MenuItem>
                        )
                      }
                    </SelectField>
                  </Grid>
                </React.Fragment>
              ) : ''
          }

          <Grid item md={3} sm={12} xs={12}>
            <Button
              variant="contained"
              type="button"
              color="primary"
              size="large"
              onClick={() => handleTable("ADD")}
              className={classes.addButton}
              startIcon={<Save />}
              disabled={state.disabledForm}
            >
              {texts.body.buttons.addProduct.title}
            </Button>
          </Grid>

          {/* ----- Form: item **concepts table rendering ------*/}
          <Grid item xs={12}>
            <ItemTable rows={state.selectedItems} handleTable={handleTable} disabledForm={state.disabledForm} />
          </Grid>

          {/* ----- Form: total ------*/}
          <Grid item md={4} sm={12} xs={12}>
            <TextField
              disabled={true}
              name={texts.body.field.total.name}
              label={texts.body.field.total.placeholder}
              value={Utils.formatNumber(state.form.total)}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.total.helperText}
            />
          </Grid>

          {/* ----- Form: total taxes ------*/}
          <Grid item md={4} sm={6} xs={12}>
            <TextField
              disabled={true}
              name={texts.body.field.totalTaxes.name}
              label={texts.body.field.totalTaxes.placeholder}
              value={Utils.formatNumber(state.form.totalTaxes)}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.totalTaxes.helperText}
            />
          </Grid>

          {/* ----- Form: total to pay ------*/}
          <Grid item md={4} sm={6} xs={12}>
            <TextField
              disabled={true}
              name={texts.body.field.totalToPay.name}
              label={texts.body.field.totalToPay.placeholder}
              value={Utils.formatNumber(state.form.totalToPay)}
              variant='outlined'
              fullWidth
              onChange={handleChange}
              helperText={texts.body.field.totalToPay.helperText}
            />
          </Grid>

          {/* --- Schedule --- */}

          {
            (!state.disabledForm && userContext.permissions.includes(permissions.electronic_bill.schedule_create)) ?
            (
              <Grid item xs={12}>
                {
                  state.form.scheduleForm ? (
                    <Card>
                      <CardHeader
                        avatar={
                          <Avatar className={classes.avatarSchedule} aria-label="recipe" >
                            <Schedule color='primary' />
                          </Avatar>
                        }
                        action={
                          <IconButton
                            aria-label="Quitar programación"
                            onClick={() => setState({
                              ...state,
                              form: {
                                ...state.form,
                                scheduleForm: undefined
                              },
                              scheduledBill: false
                            })}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        }
                        title={state.form.scheduleForm.name}
                        subheader={state.form.scheduleForm.startDate}
                      />
                    </Card>
                  ) : (
                    <Box
                      className={classes.clickable}
                      onClick={() => handleScheduleModal(true)}
                    >
                      <Card>
                        <CardHeader
                          avatar={
                            <Avatar className={classes.avatarSchedule} aria-label="recipe" >
                              <Schedule color='primary' />
                            </Avatar>
                          }
                          title={texts.body.subtitles.scheduleTitle}
                          subheader={texts.body.subtitles.scheduleDescription}
                        />
                      </Card>
                    </Box>
                  )
                }
              </Grid>
            ) : ''
          }

          {
            state.preview !== undefined ?
              (
                <Grid item xs={12} className={classes.alignRight}>
                  <Button 
                    variant="text" 
                    color="primary"
                    onClick={onPreviewBill}
                    style={{ textDecoration: 'underline' }}
                  >
                    Descargar PDF de la factura
                  </Button>
                </Grid>
              ) : ''
          }
        </Grid>

        {
          !state.disabledForm ?
          (
            <FormActions>
              <Button type="button" variant="outlined" color="secondary" startIcon={<Cancel />} onClick={cancel}>
                Cancelar
              </Button>
              <Button type="submit" variant="contained" color="primary" startIcon={<NoteAdd />}>
                Emitir factura
              </Button>
            </FormActions>
          ) : ''
        }
      </form>
      {
        state.loading ?
          (
            <div className={classes.loadingContainer}>
              <Box>
                <CircularProgress />
              </Box>
            </div>
          ) : ''
      }
      <ScheduleModal show={state.showScheduleModal} handleScheduleForm={handleScheduleForm} handleModal={handleScheduleModal} />
    </PageContainer>
  )
}
